import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../../context/AuthContext";
import { hasFullAccessUser } from "../../../../lib/screenAccess";
import { painelService, PainelData } from "../../../../services/painelService";
import { lojaService } from "../../../../services/lojaService";
import { Loja } from "../../../../types/loja";
import { useFornoEvents } from "../../../../hooks/useFornoEvents";
import { useEntregaPainelEvents } from "../../../../hooks/useEntregaPainelEvents";
import { subscribePainelRefresh } from "../../../../utils/painelRefreshBroadcast";
import {
  mapPainelParaColunasCliente,
  ClientePedidoStatusRow,
} from "../utils/mapPainelParaColunasCliente";

interface RetiradaPedidosClienteContextData {
  emPreparo: ClientePedidoStatusRow[];
  prontos: ClientePedidoStatusRow[];
  lojas: Loja[];
  isLoading: boolean;
  selectedLojaId?: number;
  /** Painel de loja aberto — só usado quando o usuário é Administrador (`showLojaSelectorUI` na view). */
  lojaPickerOpen: boolean;
  lojaSelectorVisible: boolean;
  selectedLojaNome?: string;
  toggleLojaPicker: () => void;
  selectAllLojas: () => void;
  selectLoja: (id: number) => void;
}

const RetiradaPedidosClienteContext = createContext<
  RetiradaPedidosClienteContextData | undefined
>(undefined);

export const RetiradaPedidosClienteProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { user } = useAuth();
  const isAdministrador = hasFullAccessUser(user);

  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    () => user?.lojaId
  );
  const [lojaPickerOpen, setLojaPickerOpen] = useState(false);

  const effectiveLojaId = useMemo(() => {
    if (!isAdministrador) return user?.lojaId;
    return selectedLojaId;
  }, [isAdministrador, selectedLojaId, user?.lojaId]);

  const { data: lojas = [], isLoading: isLoadingLojas } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const fetchPainelKey = [
    "painel-tv-retirada",
    effectiveLojaId ?? "all",
  ] as const;

  const {
    data: painelData,
    isLoading: isLoadingPainel,
    refetch,
  } = useQuery({
    queryKey: fetchPainelKey,
    queryFn: () => painelService.getPainel(effectiveLojaId),
    enabled: isAdministrador || user?.lojaId != null,
  });

  const onStreamEvent = useCallback(() => {
    void refetch();
  }, [refetch]);

  useFornoEvents(onStreamEvent, effectiveLojaId ?? undefined);
  useEntregaPainelEvents(onStreamEvent, effectiveLojaId ?? undefined);

  useEffect(() => {
    return subscribePainelRefresh(() => {
      void refetch();
    });
  }, [refetch]);

  const { emPreparo, prontos } = useMemo(
    () => mapPainelParaColunasCliente((painelData as PainelData) ?? null),
    [painelData]
  );

  const selectedLojaNome = useMemo(() => {
    if (effectiveLojaId == null) return undefined;
    return lojas.find((l) => l.id === effectiveLojaId)?.nome;
  }, [lojas, effectiveLojaId]);

  const toggleLojaPicker = useCallback(() => {
    setLojaPickerOpen((prev) => !prev);
  }, []);

  const selectAllLojas = useCallback(() => {
    setSelectedLojaId(undefined);
    setLojaPickerOpen(false);
  }, []);

  const selectLoja = useCallback((id: number) => {
    setSelectedLojaId(id);
    setLojaPickerOpen(false);
  }, []);

  const isLoading =
    isLoadingLojas ||
    (isLoadingPainel &&
      painelData === undefined &&
      (isAdministrador || user?.lojaId != null));

  const lojaSelectorVisible = isAdministrador && lojaPickerOpen;

  const value = useMemo(
    () => ({
      emPreparo,
      prontos,
      lojas,
      isLoading,
      selectedLojaId: effectiveLojaId,
      lojaPickerOpen,
      lojaSelectorVisible,
      selectedLojaNome,
      toggleLojaPicker,
      selectAllLojas,
      selectLoja,
    }),
    [
      emPreparo,
      prontos,
      lojas,
      isLoading,
      effectiveLojaId,
      lojaPickerOpen,
      lojaSelectorVisible,
      selectedLojaNome,
      toggleLojaPicker,
      selectAllLojas,
      selectLoja,
    ]
  );

  return (
    <RetiradaPedidosClienteContext.Provider value={value}>
      {children}
    </RetiradaPedidosClienteContext.Provider>
  );
};

export function useRetiradaPedidosCliente(): RetiradaPedidosClienteContextData {
  const ctx = useContext(RetiradaPedidosClienteContext);
  if (!ctx) {
    throw new Error(
      "useRetiradaPedidosCliente must be used within RetiradaPedidosClienteProvider"
    );
  }
  return ctx;
}
